import React, { FC, useState, useEffect, useMemo, useContext, useRef, useCallback } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { OptionsType } from 'react-select';
import { Grid, List, Button, Typography, RadioGroup, ListItem, Tooltip } from '@material-ui/core';
import { LawLink, Loading, Panel, Transition } from '../../../../../Components';
import { CheckboxField, DateField, SelectField,TextAreaField, TextField } from '../../../../../Components/Field';
import { FormFieldRef } from '../../../../../Components/Field/Field.props';
import { SelectOption, TypedFormFieldValue } from '../../../../../Components/Form/Form.types';
import { ComplianceReportQuestion } from '../Question';
import { ComplianceReportGroup, ComplianceReportCustomerQuestions } from '../Group';
import { useList } from '../../../../../Hooks/useList';
import { useChecklist } from '../../../../../Hooks/useChecklist';
import { UserContext } from '../../../../../Context/UserContext/UserContext';
import { AptorApi, useAptorApi } from '../../../../../Api';
import { PropertyError } from '../../../../../Api/AptorApi';
import { useApiValidationSnackbar } from '../../../../../Api/useValidationSnackbar';
import { useStyles } from './CreateReport.styles';
import { useReportSavedState } from './ReportState';
import moment from 'moment';
import { useConfirmation } from '../../../../../Context/ConfirmationContext/ConfirmationContext';
import { ResultCompliance } from '../Result';
import { ComplianceReportCheckbox } from '../Checkbox';
import { useParams } from "react-router-dom";
import { entityToSelectOption } from "../../../../../Utilities";
import { NotesView } from '../../OverviewNotes/View';
import { InfoOutlined } from '@material-ui/icons';
export type compliance = 'yes' | 'deficiency' | 'no';

export interface IChecklist {
  name: string;
  items: Array<IQuestion | IGroup>;
  companyUnitQuestions?: IQuestion[];
  isLightVersion?: boolean;
  lightVersionQuestion?: {
    key: string;
    selectedValue?: compliance;
    comment?: string;
    options: Array<{ value: compliance; translationKey: string }>;
  };
  date?: any;
  companyUnit?: any;
  unPlanned?: any;
  otherParticipants?: any;
  summary?: any
}

interface IAnswer {
  compliance?: compliance;
  comment?: string;
}

export interface IQuestion extends IAnswer {
  id: number;
  name: string;
  type: 'question';
  hidden: boolean;
  compliance?: any;
  comment?: any;
  groupItems?: any;
}

interface IGroup {
  id: number;
  name: string;
  type: 'group';
  groupItems: IQuestion[];
  compliance?: any;
  comment?: any;
  hidden?: any
}

export interface IReport {
  companyUnitId?: number;
  controlDate?: Date;
  unplanned: boolean;
  participants: number[];
  otherParticipants: string[];
  summary?: string;
  comment?: string;
  lightVersionCompliance?: compliance;
  lightVersionQuestionComment?: string;
  questionAnswers: any[];
  companyUnitQuestionAnswers: {
    companyUnitQuestionId: number;
    question: string;
    compliance?: compliance;
    comment?: string;
  }[];
}

interface IReplicationSource {
  planId: number;
  reportId: number;
  type: number;
  legislationId: number;
  title: string;
  isLightVersion: boolean;
  companyUnit: string;
  controller: string;
  controlDate?: Date | string;
}

interface IReplicationSourceData {
  items?: Array<IQuestion | IGroup>;
  companyUnitQuestions?: IQuestion[];
  summary?: string;
  comment?: string;
  isLightVersion: boolean;
  lightVersionCompliance?: compliance;
  lightVersionQuestionComment?: string;
  lightVersionQuestion?: {
    selectedValue?: compliance;
    comment?: string;
  };
}

const LawReplicationType = 0;
const RequirementReplicationType = 1;

const formatReplicationDate = (date?: Date | string) => {
  if (!date) return '';

  const parsedDate = moment(date);
  return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : '';
};

const getReplicationSourceLabel = (source: IReplicationSource) => {
  return [formatReplicationDate(source.controlDate), source.companyUnit, source.controller]
    .filter(Boolean)
    .join(' - ');
};

const ignoreReplicationSourceFieldChange = () => undefined;

interface IReportProps {
  titleKey: string;
  lawId?: number;
  requirementId?: number;
  summaryKey: string;
  storageKey: string;
  loaded: boolean;
  units: OptionsType<SelectOption>;
  fetchComplianceUsers: (companyUnitId: number) => Promise<OptionsType<SelectOption>>;
  checklist?: IChecklist;
  loadingChecklist: boolean;
  disableHide?: boolean;
  submitAction: (unitId: number, reportId: any, a: AptorApi, report: IReport) => Promise<void>;
  onCreateSuccess?: () => void;
  onCompanyUnitChange: (id?: number) => void;
  LawComplianceData?: any,
  setLoaded?: any,
  info?: any
}

const renderIntervalDays =
  (classes: Record<'outsideInterval', string>, start?: moment.Moment, end?: moment.Moment) =>
    (
      day: moment.Moment | null,
      _selectedDate: moment.Moment | null,
      _dayInCurrentMonth: boolean,
      dayComponent: JSX.Element,
    ) => {
      const dayIsBetween = day?.isBetween(start, end, undefined, '[]') ?? false;

      return <div className={`${!dayIsBetween ? classes.outsideInterval : ''}`}>{dayComponent}</div>;
    };

const getChecklistItemType = (item: IQuestion | IGroup | any) => `${item?.type ?? ''}`.toLowerCase();

export const CreateComplianceReport: FC<IReportProps> = ({
  lawId,
  requirementId,
  titleKey,
  summaryKey,
  storageKey,
  loaded,
  loadingChecklist,
  units,
  fetchComplianceUsers,
  checklist,
  disableHide,
  submitAction,
  onCreateSuccess,
  onCompanyUnitChange,
  LawComplianceData,
  setLoaded,
  info
}) => {
  const restored = useRef<boolean>(false);
  const { api, abortController } = useAptorApi();
  const { getCompanyUnitName, customerId, applicationVersion } = useContext(UserContext);
  const { formatMessage } = useIntl();
  const { notifySuccess, notifyUnhandledException, notifyValidationErrors } = useApiValidationSnackbar();
  const [items, loadItems, updateQuestion] = useChecklist<IQuestion, IGroup>();
  const [companyUnitQuestions, loadCompanyUnitQuestions, , updateCompanyUnitQuestion] = useList<IQuestion>();
  const isOldVersion = applicationVersion?.value === 1;

  const getUtcNowDate = () => {
    const now = moment(new Date());
    return moment.utc({ year: now.year(), month: now.month(), day: now.date() }).toDate();
  };
  const [date, setDate] = useState<TypedFormFieldValue<Date> | undefined>({ value: getUtcNowDate() });
  const [companyUnit, setCompanyUnit] = useState<TypedFormFieldValue<number> | undefined>();
  const [otherParticipants, setOtherParticipants] = useState<TypedFormFieldValue<number | string>[]>();
  const [summary, setSummary] = useState<TypedFormFieldValue<string>>();
  const [comment, setComment] = useState<TypedFormFieldValue<string>>();
  const [interval, setInterval] = useState<{ start?: moment.Moment; end?: moment.Moment }>();
  const [unplanned, setUnplanned] = useState<boolean>(true);
  const [lightVersionCompliance, setLightVersionCompliance] = useState<compliance | undefined>();
  const [questionComment, setQuestionComment] = useState<TypedFormFieldValue<string>>();
  const [replicationSources, setReplicationSources] = useState<IReplicationSource[]>([]);
  const [replicationSource, setReplicationSource] = useState<TypedFormFieldValue<string> | undefined>();
  const match = useParams<{ planid: string, id: string }>();
  const [users, setUsers] = useState<OptionsType<SelectOption>>();
  const summaryRef = useRef<FormFieldRef<string> | null>(null);
  const commentRef = useRef<FormFieldRef<string> | null>(null);
    const questionCommentRef = useRef<FormFieldRef<string> | null>(null);
  const { updateState, storeState, clearState } = useReportSavedState(
    units,
    fetchComplianceUsers,
    setDate,
    setCompanyUnit,
    setOtherParticipants,
    setSummary,
    storageKey,
    setComment
  );

  const classes = useStyles();
  const path = window.location.pathname?.split("/")
  const checkEdit = path[path.length - 1]
  const currentReplicationType = requirementId ? RequirementReplicationType : LawReplicationType;
  const planId = match?.planid;
  const hasChecklist = !!checklist;
  useEffect(() => {
    onCompanyUnitChange(companyUnit?.value);
    setOtherParticipants(undefined);
  }, [companyUnit, onCompanyUnitChange, clearState]);
  const prefillComponyUnit = () => {
    if (!customerId || !planId) {
      return;
    }
    setLoaded(false)

    api.getAllcompliancePlan(customerId, { filters: {}, search: "" }).then((response: any) => {
      let checkCurrentplan = response?.customerPlans?.filter((data: any) => data.id == planId)
      if (checkCurrentplan?.length) {
        setCompanyUnit(entityToSelectOption(checkCurrentplan[0].customerCompanyUnitId))
        setUnplanned(checkCurrentplan[0].isDirect)
      }
      setLoaded(true)
    }).catch(() => {
      setLoaded(true);
    })
  }
  useEffect(() => {
    prefillComponyUnit()
  }, [customerId, planId])
  useEffect(() => {
    const fetchReplicationSources = async () => {
      if (checkEdit === 'edit' || !hasChecklist) {
        setReplicationSources([]);
        return;
      }

      try {
        const sources = await api.searchEvaluationControlReplicationSources<IReplicationSource[]>();
        if (!abortController.current.signal.aborted) {
          setReplicationSources(sources);
        }
      } catch {
        if (!abortController.current.signal.aborted) {
          setReplicationSources([]);
        }
      }
    };

    fetchReplicationSources();
  }, [api, abortController, checkEdit, currentReplicationType, hasChecklist, match.id]);
  useEffect(() => {
    if (checkEdit === 'edit' && LawComplianceData !== "") {
      setLoaded(false)
      if (path?.length && path[5] === "other-requirements") {
        api.getUserCompanyUnitsForRequirementCompliance(Number(match.id)).then((userCompanyUnits) => {
          if (!abortController.current.signal.aborted) {
            const companyUnitOptions: any = userCompanyUnits.items.map(entityToSelectOption)
            const currentCompanyUnit: any = companyUnitOptions?.filter((val: any) => val.value === LawComplianceData?.companyUnit)
            if (currentCompanyUnit?.length) {
              setCompanyUnit(currentCompanyUnit[0])
            }
            const complianceDate: any = moment(LawComplianceData.date)
            setDate({ value: complianceDate })
            setSummary({ value: LawComplianceData.summary })
            setComment({ value: LawComplianceData.comment })
            setLightVersionCompliance(LawComplianceData.lightVersionCompliance ?? LawComplianceData.lightVersionQuestion?.selectedValue)
            setQuestionComment({ value: LawComplianceData.lightVersionQuestionComment ?? LawComplianceData.lightVersionQuestion?.comment ?? '' });
            setLoaded(true)
          }
        })
      } else {
        api.getUserCompanyUnitsForLawCompliance(Number(match.id)).then((userCompanyUnits) => {
          if (!abortController.current.signal.aborted) {
            const companyUnitOptions: any = userCompanyUnits.items.map(entityToSelectOption)
            const currentCompanyUnit: any = companyUnitOptions?.filter((val: any) => val.value === LawComplianceData?.companyUnit)
            if (currentCompanyUnit?.length) {
              setCompanyUnit(currentCompanyUnit[0])
            }
            const complianceDate: any = moment(LawComplianceData.date)
            setDate({ value: complianceDate })
            setSummary({ value: LawComplianceData.summary })
            setComment({ value: LawComplianceData.comment })
            setLightVersionCompliance(LawComplianceData.lightVersionCompliance ?? LawComplianceData.lightVersionQuestion?.selectedValue)
            setQuestionComment({ value: LawComplianceData.lightVersionQuestionComment ?? LawComplianceData.lightVersionQuestion?.comment ?? '' });
            setLoaded(true)
          }
        })
      }
    }
  }, [LawComplianceData])

  useEffect(() => {
    if (checklist) {
      setLightVersionCompliance(checklist.lightVersionQuestion?.selectedValue ?? LawComplianceData?.lightVersionCompliance ?? LawComplianceData?.lightVersionQuestion?.selectedValue);
      setQuestionComment({ value: checklist.lightVersionQuestion?.comment ?? LawComplianceData?.lightVersionQuestionComment ?? LawComplianceData?.lightVersionQuestion?.comment ?? '' });
      let items = [...checklist.items];
      if (checkEdit === "edit") {
        const updateItems = (item: any) => {
          var updatedItem = { ...item };
          LawComplianceData?.items?.forEach((value: any) => {
            if (updatedItem.id === value.id) {
              updatedItem['hidden'] = value["hidden"] || false;
              if (value["compliance"] != "") {
                updatedItem['compliance'] = value["compliance"];
              }
              if (value["comment"] != "") {
                updatedItem['comment'] = value["comment"];
              }
              if (value["groupItems"]?.length) {
                const updatednestedItem = (nestitem: any) => {
                  var myitem = { ...nestitem }
                  myitem['hidden'] = myitem["hidden"] || false;
                  if (myitem["compliance"] == "") {
                    delete myitem["compliance"]
                  }
                  if (myitem["comment"] == "") {
                    delete myitem["comment"]
                  }
                  myitem['groupItems'] = myitem["groupItems"].map(updatednestedItem);
                  return myitem

                }
                updatedItem['groupItems'] = value["groupItems"].map(updatednestedItem);
              }
            }
          });
          return updatedItem;
        };
        const data = items.map(updateItems);
        items = data;
      }
      const companyUnitQuestionItems = checklist.companyUnitQuestions ? [...checklist.companyUnitQuestions] : [];
      if (restored.current === false) {
        updateState({ items: items, companyUnitQuestions: companyUnitQuestionItems });
        restored.current = true;
      }
      loadItems(items);
      loadCompanyUnitQuestions(companyUnitQuestionItems);
    }
  }, [checklist, loadItems, loadCompanyUnitQuestions, updateState, LawComplianceData]);
  useEffect(() => {
    storeState({
      items,
      companyUnitQuestions,
      date: date?.value,
      companyUnit: companyUnit?.value,
      otherParticipants: otherParticipants?.map((x) => x.value) ?? [],
      summary: summary?.value,
      comment: comment?.value
    });
  }, [storeState, items, companyUnitQuestions, date, companyUnit, otherParticipants, summary, comment]);

  useEffect(() => {
    const fetchUsers = async () => {
      if (companyUnit) {
        const result = await fetchComplianceUsers(companyUnit.value);
        setOtherParticipants(result?.filter((x) => LawComplianceData?.otherParticipants?.includes(x.value)).map((x) => ({ value: x.value, label: x.label })) ?? []);
        setUsers(result);
      }
    };

    fetchUsers();
  }, [fetchComplianceUsers, companyUnit]);

  useEffect(() => {
    const fetchInterval = async () => {
      if (lawId && companyUnit) {
        const result = await api.getLawComplianceInterval(lawId, companyUnit.value);
        setInterval({
          start: result.start ? moment(result.start) : undefined,
          end: result.end ? moment(result.end) : undefined,
        });
      }
      if (requirementId && companyUnit) {
        const result = await api.getRequirementComplianceInterval(requirementId, companyUnit.value);
        setInterval({
          start: result.start ? moment(result.start) : undefined,
          end: result.end ? moment(result.end) : undefined,
        });
      }
    };
    fetchInterval();
  }, [companyUnit, api, lawId, requirementId]);

  const setSummaryValue = useCallback((value: string) => {
    const fieldValue = { value };
    setSummary(fieldValue);
    summaryRef.current?.setValue(fieldValue);
  }, []);

  const setCommentValue = useCallback((value: string) => {
    const fieldValue = { value };
    setComment(fieldValue);
    commentRef.current?.setValue(fieldValue);
  }, []);

  const applyReplicationSource = useCallback((sourceData: IReplicationSourceData) => {
    setSummaryValue(sourceData.summary ?? '');
    setCommentValue(sourceData.comment ?? '');

    if (checklist?.isLightVersion) {
      setLightVersionCompliance(sourceData.isLightVersion
        ? sourceData.lightVersionCompliance ?? sourceData.lightVersionQuestion?.selectedValue
        : undefined);
        setQuestionComment({
        value: sourceData.isLightVersion
          ? sourceData.lightVersionQuestionComment ?? sourceData.lightVersionQuestion?.comment ?? ''
          : '',
      });
      return;
    }

    if (sourceData.isLightVersion) {
      loadItems(checklist?.items ?? []);
      loadCompanyUnitQuestions(checklist?.companyUnitQuestions ?? []);
      return;
    }

    loadItems(sourceData.items ?? []);
    loadCompanyUnitQuestions(sourceData.companyUnitQuestions ?? []);
  }, [checklist, loadCompanyUnitQuestions, loadItems, setCommentValue, setSummaryValue]);

  const replicationSourceOptions = useMemo(() => {
    return replicationSources
      .filter((source) =>
        source.type === currentReplicationType &&
        source.legislationId === Number(match.id)
      )
      .map((source) => ({
        value: `${source.type}:${source.reportId}`,
        label: getReplicationSourceLabel(source),
      }));
  }, [currentReplicationType, match.id, replicationSources]);

  const handleReplicationSourceChange = useCallback(async (option: SelectOption | null) => {
    const selected = option
      ? { value: option.value as string, label: option.label, isValid: true }
      : undefined;

    setReplicationSource(selected);
    if (!selected?.value) {
      setSummaryValue('');
      setCommentValue('');
      setLightVersionCompliance(undefined);
       setQuestionComment(undefined);
      loadItems(checklist?.items ?? []);
      loadCompanyUnitQuestions(checklist?.companyUnitQuestions ?? []);
      return;
    }

    const [type, reportId] = selected.value.split(':').map(Number);
    const source = replicationSources.find((item) => item.type === type && item.reportId === reportId);
    if (!source) {
      return;
    }
    try {
      const sourceData = type === LawReplicationType
        ? await api.getLawPortalLawChecklistWithData<IReplicationSourceData>(source.planId, source.legislationId, source.reportId)
        : await api.getLawPortalRequirementChecklistWithData<IReplicationSourceData>(source.planId, source.legislationId, source.reportId);
      if (!abortController.current.signal.aborted) {
        applyReplicationSource({ ...sourceData, isLightVersion: source.isLightVersion });
      }
    } catch {
      if (!abortController.current.signal.aborted) {
        setReplicationSource(undefined);
      }
    }
  }, [abortController, api, applyReplicationSource, checklist, loadCompanyUnitQuestions, loadItems, replicationSources]);

  const isOutsideInterval = useMemo(() => {
    if (!interval || (!interval.start && !interval.end)) return false;
    let isBeforeCopmliance = interval.start?.isAfter(date?.value);
    let isAfterCompliance = interval.end && moment(interval?.end)?.add(1, 'day')?.isBefore(date?.value);
    return isBeforeCopmliance || isAfterCompliance || false;
  }, [date, interval]);
  const answerIsInvalid = (answer: IAnswer, hidden: boolean) => {
    if (hidden === true) {
      return false;
    } else if (!answer.compliance) {
      return false;
    }
    return answer.compliance !== 'yes' && (answer.comment == null || answer.comment.length === 0);
  };

  const disableSave = useMemo(() => {
    if (date?.value === undefined || companyUnit?.value === undefined) {
      return true;
    }

    if (checklist?.isLightVersion) {
     return (
        lightVersionCompliance === undefined ||
        (lightVersionCompliance !== 'yes' && !questionComment?.value?.trim())
      );
    }

    return (
      items.some((item) => {
        if (getChecklistItemType(item) === 'question') {
          return answerIsInvalid(item, item.hidden);
        } else if (getChecklistItemType(item) === 'group') {
          return (item.groupItems ?? []).some((gi: any) => answerIsInvalid(gi, gi.hidden));
        }
        return false;
      }) ||
      companyUnitQuestions.some((companyUnitQuestion) => {
        return answerIsInvalid(companyUnitQuestion, false);
      })
    );
  }, [items, companyUnitQuestions, date, companyUnit, checklist?.isLightVersion, lightVersionCompliance,questionComment]);

  const submit = async () => {
    const companyUnitId = companyUnit?.value;
    if (companyUnitId !== undefined) {
      let reportId = ""
      const path = window.location.pathname?.split("/")
      const checkEdit = path[path.length - 1]
      if (checkEdit === 'edit') {
        reportId = LawComplianceData?.reportId
      }
      const action = (aptorApi: AptorApi) =>
        submitAction(companyUnitId, reportId, aptorApi, {
          companyUnitId: companyUnitId,
          controlDate: date?.value,
          unplanned: unplanned,
          participants:
            otherParticipants && otherParticipants.length
              ? otherParticipants.filter((x) => typeof x.value === 'number').map((x) => +x.value)
              : [],
          otherParticipants:
            otherParticipants && otherParticipants.length
              ? otherParticipants.filter((x) => typeof x.value === 'string').map((x) => `${x.value}`)
              : [],
          summary: summary?.value,
          comment: comment?.value,
          lightVersionCompliance,
           lightVersionQuestionComment: questionComment?.value,
          questionAnswers: checklist?.isLightVersion ? [] : items.map(itemToDtos).reduce((a, b) => a.concat(b), []),
          companyUnitQuestionAnswers: checklist?.isLightVersion ? [] : companyUnitQuestions.map((companyUnitQuestion) => ({
            companyUnitQuestionId: companyUnitQuestion.id,
            question: companyUnitQuestion.name,
            compliance: companyUnitQuestion.compliance,
            comment: companyUnitQuestion.comment,
          })),
        }).then(async (res: any) => {
          if (checkEdit === 'edit') {
            notifySuccess();
            clearState();
            onCreateSuccess && onCreateSuccess();
          } else {
            let payload = {
              LegislationId: Number(match.id),
              ReportIds: res?.id
            }
            await api.createplanReport(Number(match.planid), payload)
              .then(() => {
                notifySuccess();
                clearState();
                onCreateSuccess && onCreateSuccess();
              })
          }
        });

      const onValidation = (errors: PropertyError[]) => notifyValidationErrors(errors);
      await api.invoke(action, abortController.current, onValidation, notifyUnhandledException);
    }
  };

  const itemToDtos = (item: IQuestion | IGroup): any[] => {
    if (getChecklistItemType(item) === 'question') {
      return [
        {
          questionId: item.id,
          question: item.name,
          hidden: item.hidden,
          group: null,
          compliance: item.compliance,
          comment: item.comment,
        },
      ];
    } else {
      return item.groupItems?.map((groupItem: any) => ({
        questionId: groupItem.id,
        question: groupItem.name,
        hidden: groupItem.hidden,
        group: item.name,
        compliance: groupItem.compliance,
        comment: groupItem.comment,
      }));
    }
  };

  const confirmedChangeWarning = useRef<boolean>(true);

  useEffect(() => {
    if (companyUnit) {
      confirmedChangeWarning.current = false;
    } else {
      confirmedChangeWarning.current = true;
    }
  }, [companyUnit]);
  const { deleteConfirmation } = useConfirmation();
  const accept = useCallback(async () => {
    confirmedChangeWarning.current = true;
  }, []);
  const handleCompanyUnitWarning = useCallback(() => {
    if (confirmedChangeWarning.current) {
      return;
    }
    const confirmation = {
      title: formatMessage(
        { id: 'law-portal.compliance.form.change-warning.label' },
        { companyUnit: `${getCompanyUnitName(formatMessage) ?? ''}`.toLowerCase() },
      ),
      description: formatMessage(
        { id: 'law-portal.compliance.form.change-warning.confirmation' },
        { companyUnit: `${getCompanyUnitName(formatMessage) ?? ''}`.toLowerCase() },
      ),
    };

    deleteConfirmation(accept, { ...confirmation, hideSavedNotification: true, hideCancel: true, name: undefined });
  }, [formatMessage, deleteConfirmation, accept, getCompanyUnitName]);

  if (!loaded) {
    return <Loading />;
  }

  function flattenGroupItems(data: any[]): any[] {
    return data?.flatMap(item =>
      item?.type === "group" && item.groupItems
        ? flattenGroupItems(item?.groupItems)
        : item
    );
  }
  // const flattenedData = flattenGroupItems(items);
  const flattenedData = [...flattenGroupItems(items), ...companyUnitQuestions]
  const total = checklist?.isLightVersion ? 1 : flattenedData.filter((item) => item?.hidden == false || item?.hidden == undefined).length || 0;
  const yesCount = checklist?.isLightVersion ? (lightVersionCompliance === 'yes' ? 1 : 0) : flattenedData.filter((item) => (item as IQuestion | IAnswer)?.compliance === "yes").length || 0;
  const noCount = checklist?.isLightVersion ? (lightVersionCompliance === 'no' ? 1 : 0) : flattenedData.filter((item) => (item as IQuestion | IAnswer)?.compliance === "no").length || 0;
  const deficiencyCount = checklist?.isLightVersion ? (lightVersionCompliance === 'deficiency' ? 1 : 0) : flattenedData.filter((item) => (item as IQuestion | IAnswer)?.compliance === "deficiency").length || 0;
  // const yesPercentage = parseFloat(((yesCount / (yesCount + noCount + deficiencyCount)) * 100).toFixed(2)) || 0;
  const filledCount = yesCount + noCount + deficiencyCount;
const yesPercentage =
  total > 0
    ?filledCount>0? parseFloat(((yesCount / filledCount ) * 100).toFixed(2)):0
    : 100;
    const lightVersionCommentRequired = lightVersionCompliance === 'deficiency' || lightVersionCompliance === 'no';
  const lightVersionCommentError = lightVersionCommentRequired && !questionComment?.value?.trim()
    ? formatMessage({ id: 'law-portal.compliance.comment-required.error-message' })
    : undefined;
  const copyEvaluationInfoText = formatMessage({
    id: 'lawportal.evalution.previous.evalution.copy.info',
    defaultMessage: 'Copy responses from a previous evaluation control by selecting a previously completed control from the list. This option is only available if a previous evaluation control has been performed. To perform a new evaluation control, leave this field empty.',
  });
  return (
    <>
      {checkEdit !== 'edit'&& !isOldVersion  && (
        <Panel style={{ marginBottom: 16 }}>
          <Grid container spacing={2}>
            <Grid item sm={5} xs={12}>
              <SelectField
                key={`replication-source-${replicationSource?.value ?? 'empty'}`}
                name="replication-source"
                label={formatMessage({ id: 'law-portal.compliance.form.replication-source.label' })}
                errorTexts={[]}
                icon={
                  <Tooltip title={copyEvaluationInfoText} placement="right-start">
                    <span
                      tabIndex={0}
                      role="img"
                      aria-label={copyEvaluationInfoText}
                      style={{ display: 'inline-flex', marginLeft: 4, verticalAlign: 'middle',marginBottom: 4 }}
                    >
                      <InfoOutlined />
                    </span>
                  </Tooltip>
                }
                initialState={replicationSource}
                options={replicationSourceOptions}
                isClearable
                onChange={ignoreReplicationSourceFieldChange}
                onhandle={handleReplicationSourceChange}
              />
            </Grid>
          </Grid>
        </Panel>
      )}
      <Panel titleKey={titleKey} >
      {/* {unplanned === false && interval && (interval.start || interval.end) && (
        <Typography variant="h4" component="h3" style={{ marginBottom: '1em' }}>
          {formatMessage({ id: 'law-portal.compliance.form.planned' })} {formatMessage({ id: titleKey })}:{' '}
          {interval?.start?.format('ll') ?? ''} - {interval?.end?.format('ll') ?? ''}
        </Typography>
      )} */}
      <Grid container spacing={2} >
        <Grid item sm={5} xs={12}>
          {companyUnit ? (
            <SelectField
              required
              name="company-unit"
              label={getCompanyUnitName(formatMessage)}
              errorTexts={[]}
              initialState={companyUnit}
              options={units}
              disable={true}
              onChange={setCompanyUnit}
              onMenuOpen={handleCompanyUnitWarning}
            />
          ) : (
            <SelectField
              required
              name="company-unit"
              label={getCompanyUnitName(formatMessage)}
              errorTexts={[]}
              initialState={companyUnit}
              options={units}
              disable={checkEdit === "edit" ? true : false}
              onChange={setCompanyUnit}
              onMenuOpen={handleCompanyUnitWarning}
            />
          )}
        </Grid>
        <Grid item sm={5} xs={12}>
          <DateField
            required
            onRenderDay={
              (interval?.start || interval?.end) && unplanned === false
                ? renderIntervalDays(classes, interval.start, interval.end)
                : undefined
            }
            name="date"
            label={formatMessage({ id: 'law-portal.compliance.form.date.label' })}
            errorTexts={[]}
            // helperText={
            //   ((unplanned === false && isOutsideInterval && (
            //     <Typography variant="caption" className={classes.warningText}>
            //       {formatMessage({ id: 'law-portal.compliance.form.date.warning' })}
            //     </Typography>
            //   )) ||
            //     undefined) as any
            // }
            initialState={date}
            onChange={setDate}
          />
        </Grid>

        {users && (
          <Grid item sm={5} xs={12}>
            <SelectField
              isMulti
              creatable
              createLabel={formatMessage({ id: 'law-portal.compliance.form.other-participant' })}
              name="participants"
              label={formatMessage({ id: 'law-portal.compliance.form.other-participants.label' })}
              errorTexts={[]}
              initialState={otherParticipants}
              key={otherParticipants?.map((x) => x.value).join('-')}
              options={users}
              onChange={setOtherParticipants}
            />
          </Grid>
        )}
        {/* <Grid item sm={7} xs={12} alignItems='center' style={{ paddingTop: 30 }}>
          <CheckboxField
            label={formatMessage({ id: 'law-portal.compliance.form.unplanned.label' })}
            name="unplanned"
            errorTexts={[]}
            initialValue={unplanned}
            onChange={(val) => setUnplanned(val?.value ?? false)}
          />
        </Grid> */}
         <Grid item xs={12}>
  {info?.companyUnits&&<Panel titleKey="law-portal.evaluation-control.overview-notes.label">
          <NotesView companyUnits={info?.companyUnits} selectCompanyUnit={companyUnit} />
        </Panel>}
         </Grid>

        <Grid item xs={12}>
          <TextField
            ref={summaryRef}
            name="summary"
            label={formatMessage({ id: summaryKey })}
            rows={8}
            multiline
            errorTexts={[]}
            initialState={summary}
            onChange={setSummary}
          />
        </Grid>
      </Grid>
      {companyUnit && (
        <Transition isLoading={loadingChecklist} hideSpinner>
          <List className={classes.list}>
            {checklist?.isLightVersion && checklist.lightVersionQuestion ? (
              <ListItem className={classes.lightQuestion}>
                <div className={classes.lightQuestionText}>
                  <TextAreaField
                    key={`${lightVersionCompliance ?? ''}`}
                    ref={questionCommentRef}
                    name="light-version-comment"
                    label={`1. ${formatMessage({ id: checklist.lightVersionQuestion.key })}`}
                    required={lightVersionCommentRequired}
                    error={!!lightVersionCommentError}
                    errorTexts={lightVersionCommentError ? [lightVersionCommentError] : []}
                    initialState={questionComment}
                    onChange={setQuestionComment}
                    rows={4}
                  />
                </div>
                <div className={classes.lightQuestionActions}>
                  <RadioGroup
                    row
                    className={classes.lightQuestionOptions}
                    value={lightVersionCompliance ?? ''}
                    onChange={(event) => setLightVersionCompliance(event.target.value as compliance)}
                  >
                    {checklist.lightVersionQuestion.options.map((option) => (
                      <ComplianceReportCheckbox
                        key={option.value}
                        type={option.value}
                        value={lightVersionCompliance}
                        labelKey={option.translationKey}
                      />
                    ))}
                  </RadioGroup>
                </div>
              </ListItem>
            ) : items.map((item: any, index: any) =>
              getChecklistItemType(item) === 'question' ? (
                <ComplianceReportQuestion
                  key={`question-${item.id}`}
                  {...item}
                  index={index + 1}
                  disableHide={disableHide}
                  onChange={updateQuestion}
                />
              ) : (
                <ComplianceReportGroup
                  key={`group-${item.id}`}
                  {...item}
                  index={index + 1}
                  disableHide={disableHide}
                  onChange={updateQuestion}
                />
              ),
            )}

            {!checklist?.isLightVersion && companyUnitQuestions.length > 0 && (
              <ComplianceReportCustomerQuestions
                key="customer-questions"
                questions={companyUnitQuestions}
                onChange={updateCompanyUnitQuestion}
              />
            )}
          </List>
          <ResultCompliance complied={yesCount} deficiency={deficiencyCount} deviation={noCount} fulfilled={yesPercentage} />
          <Grid container spacing={2} style={{ marginTop: 20 }}>
            <Grid item xs={12}>
              <TextField
                ref={commentRef}
                name="comment"
                label={formatMessage({ id: "law-portal.our-laws.compliance.comment" })}
                rows={8}
                multiline
                errorTexts={[]}
                initialState={comment}
                onChange={setComment}
              />
            </Grid>
            <Grid item xs={12}>
              <Grid container justify="flex-end">
                <Button disabled={disableSave} variant="outlined" onClick={submit}>
                  <FormattedMessage id="form.save" />
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Transition>
      )}
    </Panel>
    </>
  );
};
